import React from 'react';
import { NavLink, Outlet, Navigate, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, ClipboardList, FolderLock, BellRing, MessageSquare, LifeBuoy,
  LogOut, Shield, Eye,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/dashboard/policies', label: 'My Policies', icon: FileText },
  { to: '/dashboard/quotes', label: 'My Quotes', icon: ClipboardList },
  { to: '/dashboard/documents', label: 'Documents', icon: FolderLock },
  { to: '/dashboard/renewals', label: 'Renewals', icon: BellRing },
  { to: '/dashboard/messages', label: 'Messages', icon: MessageSquare },
  { to: '/dashboard/support', label: 'Support', icon: LifeBuoy },
];

export default function DashboardLayout() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="bg-[#f7fafd] min-h-screen">
      <div className="container-x pt-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-6">
          <aside className="bg-white border border-slate-200 rounded-2xl p-4 h-fit sticky top-24">
            <div className="px-2 py-3 border-b border-slate-100 mb-3">
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-full bg-[#0d3a8a] text-white inline-flex items-center justify-center">
                  <Eye size={16} />
                </span>
                <div>
                  <p className="font-semibold text-[#0b1f3a] text-sm leading-tight">{user.name}</p>
                  <p className="text-xs text-slate-500 leading-tight">{user.email}</p>
                </div>
              </div>
            </div>

            <nav className="flex flex-col gap-1">
              {navItems.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'bg-[#eef4fb] text-[#0d3a8a] font-semibold'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`
                  }
                >
                  <Icon size={16} />
                  {label}
                </NavLink>
              ))}

              {user.role === 'admin' && (
                <NavLink
                  to="/admin"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-amber-700 bg-amber-50 hover:bg-amber-100 mt-2"
                >
                  <Shield size={16} /> Webmaster Admin
                </NavLink>
              )}
            </nav>

            <button
              onClick={() => { logout(); navigate('/'); }}
              className="w-full mt-3 flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-slate-500 hover:bg-slate-50 border-t border-slate-100 pt-4"
            >
              <LogOut size={16} /> Logout
            </button>
          </aside>

          <main>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
